/**
 * sessionStorage persistence for silent OAuth auto-retry (no Supabase imports).
 */

import {
  OAUTH_AUTO_RETRY_ATTEMPTED_STORAGE_KEY,
  OAUTH_RESTART_CONFIG_STORAGE_KEY,
} from '../constants/oauth.constants'

export type OAuthRestartProvider = 'google' | 'facebook' | 'apple' | 'azure'

export type OAuthRestartConfig = {
  provider: OAuthRestartProvider
  queryParams?: Record<string, string>
  useLinkIdentity?: boolean
}

export function persistOAuthRestartConfig(config: OAuthRestartConfig): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.setItem(OAUTH_RESTART_CONFIG_STORAGE_KEY, JSON.stringify(config))
    sessionStorage.removeItem(OAUTH_AUTO_RETRY_ATTEMPTED_STORAGE_KEY)
  } catch {
    // ignore storage errors
  }
}

export function clearOAuthAutoRetryState(): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.removeItem(OAUTH_AUTO_RETRY_ATTEMPTED_STORAGE_KEY)
    sessionStorage.removeItem(OAUTH_RESTART_CONFIG_STORAGE_KEY)
  } catch {
    // ignore storage errors
  }
}

export function readOAuthRestartConfig(): OAuthRestartConfig | null {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(OAUTH_RESTART_CONFIG_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as OAuthRestartConfig
    if (!parsed?.provider) return null
    return parsed
  } catch {
    return null
  }
}

export function hasOAuthAutoRetryBeenAttempted(): boolean {
  if (typeof sessionStorage === 'undefined') return false
  try {
    return sessionStorage.getItem(OAUTH_AUTO_RETRY_ATTEMPTED_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

export function markOAuthAutoRetryAttempted(): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.setItem(OAUTH_AUTO_RETRY_ATTEMPTED_STORAGE_KEY, '1')
  } catch {
    // ignore storage errors
  }
}

const SILENT_OAUTH_AUTO_RETRY_ERRORS = new Set(['server_error', 'temporarily_unavailable'])

export function shouldSilentlyAutoRetryOAuthCallback(
  error: string,
  errorCode: string | null | undefined,
  errorDescription: string | null | undefined,
  isCodeReuseFailure: (params: {
    error: string
    errorCode?: string | null
    errorDescription?: string | null
  }) => boolean,
): boolean {
  if (hasOAuthAutoRetryBeenAttempted()) return false
  if (!readOAuthRestartConfig()) return false
  if (isCodeReuseFailure({ error, errorCode, errorDescription })) return true
  // GoTrue often returns bare server_error (no error_description) on first OAuth redirect
  // behind corporate proxies or transient provider failures — one silent restart fixes it.
  return SILENT_OAUTH_AUTO_RETRY_ERRORS.has(error.toLowerCase())
}
